import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Download, Trash2, Mail, ShieldCheck } from "lucide-react";
import { DevicesSection } from "@/components/DevicesSection";
import { TwoFactorSection } from "@/components/TwoFactorSection";
import { ChangePasswordSection } from "@/components/ChangePasswordSection";

export default function AccountPrivacy() {
  const { user } = useAuth();
  const [digestOptIn, setDigestOptIn] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [password, setPassword] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    api.get("/gdpr/marketing").then(({ data }) => setDigestOptIn(data.digest_opt_in)).catch(() => {});
  }, []);

  const toggleDigest = async (v) => {
    setDigestOptIn(v);
    try {
      await api.patch("/gdpr/marketing", { digest_opt_in: v });
      toast.success(v ? "Weekly digest on" : "Weekly digest off");
    } catch {
      setDigestOptIn(!v);
      toast.error("Could not update preference");
    }
  };

  const exportData = async () => {
    setExporting(true);
    try {
      const { data } = await api.get("/gdpr/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "vismarks-my-data.json";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Your data export has downloaded");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setExporting(false);
    }
  };

  const deleteAccount = async () => {
    setDeleting(true);
    try {
      await api.post("/gdpr/delete-account", { confirm, password: password || null });
      toast.success("Account deleted. Goodbye 👋");
      setTimeout(() => window.location.assign("/"), 800);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setDeleting(false);
    }
  };

  const isLocal = user && user !== false && (user.provider || "local") === "local";

  return (
    <div className="max-w-2xl mx-auto px-6 lg:px-10 py-12" data-testid="account-privacy-page">
      <div className="text-xs uppercase tracking-widest text-[#007AFF]">Privacy &amp; data</div>
      <h1 className="mt-2 font-black tracking-tight text-4xl">Your data, your rules</h1>
      <p className="mt-3 text-neutral-400 text-sm leading-relaxed max-w-lg">
        Everything here is self-service — export a full copy of your data, manage email consent,
        or delete your account permanently. See the{" "}
        <a href="/privacy" className="text-[#007AFF] hover:underline">Privacy Policy</a> for details.
      </p>

      <div className="mt-10 rounded-xl border border-[#27272A] bg-[#121212] p-6">
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-[#007AFF]" />
          <h2 className="font-bold text-lg">Weekly digest email</h2>
        </div>
        <div className="mt-3 flex items-center justify-between gap-4">
          <p className="text-sm text-neutral-400">
            A weekly roundup of the hottest drops. Off unless you opt in — one-click unsubscribe in every email.
          </p>
          {digestOptIn !== null && (
            <Switch data-testid="digest-optin-switch" checked={digestOptIn} onCheckedChange={toggleDigest} />
          )}
        </div>
      </div>

      <div className="mt-6 rounded-xl border border-[#27272A] bg-[#121212] p-6">
        <div className="flex items-center gap-2">
          <Download className="h-4 w-4 text-[#34C759]" />
          <h2 className="font-bold text-lg">Export my data</h2>
        </div>
        <p className="mt-2 text-sm text-neutral-400">
          One-click download of your profile, orders, products and payment history as JSON (GDPR Art. 20).
        </p>
        <Button
          onClick={exportData}
          disabled={exporting}
          data-testid="export-data-btn"
          className="mt-4 rounded-full bg-white text-black hover:bg-neutral-200 h-10 px-6 font-semibold"
        >
          <Download className="h-4 w-4 mr-2" />
          {exporting ? "Preparing…" : "Download my data"}
        </Button>
      </div>

      <ChangePasswordSection />
      <TwoFactorSection />
      <DevicesSection />

      <div className="mt-6 rounded-xl border border-[#FF3B30]/40 bg-[#FF3B30]/[0.04] p-6">
        <div className="flex items-center gap-2">
          <Trash2 className="h-4 w-4 text-[#FF3B30]" />
          <h2 className="font-bold text-lg">Delete my account</h2>
        </div>
        <p className="mt-2 text-sm text-neutral-400">
          Permanent. Your products and profile are erased; order and payment records are kept for
          accounting but anonymized (GDPR Art. 17). Type <span className="font-mono text-white">DELETE</span> to confirm.
        </p>
        <div className="mt-4 space-y-3 max-w-sm">
          <Input
            data-testid="delete-confirm-input"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Type DELETE"
            className="bg-[#121212] border-[#27272A] font-mono"
          />
          {isLocal && (
            <Input
              type="password"
              data-testid="delete-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Your password"
              className="bg-[#121212] border-[#27272A]"
            />
          )}
          <Button
            onClick={deleteAccount}
            disabled={deleting || confirm.trim().toUpperCase() !== "DELETE" || (isLocal && !password)}
            data-testid="delete-account-btn"
            className="rounded-full bg-[#FF3B30] hover:bg-[#e0342a] text-white h-10 px-6 font-semibold"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            {deleting ? "Deleting…" : "Delete my account forever"}
          </Button>
        </div>
      </div>

      <div className="mt-8 flex items-start gap-2 text-xs text-neutral-500">
        <ShieldCheck className="h-4 w-4 flex-shrink-0 mt-0.5" />
        <span>
          Rights requests we can't automate (rectification, objection, complaints) — email{" "}
          <a href="mailto:privacy@vismarks.com" className="text-[#007AFF] hover:underline">privacy@vismarks.com</a>.
        </span>
      </div>
    </div>
  );
}
